import { memo, useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import { getPredefinedGroupLabel } from "@common/constants/domains/accounting/accountGroups";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { AccountRole } from "@common/types/domains/accounting/account";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerSectionData } from "@design-system/Picker/Picker.types";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { PlusIcon } from "@icons";
import {
    RequiredAccountRow,
    RoleMappingAccount,
} from "../requiredAccountsMapping";
import editorStyles from "../../editor/ChartOfAccountsTemplateEditor.module.scss";
import styles from "../RequiredAccountsMapping.module.scss";

export interface RequiredAccountsCallbacks {
    onAssign: (params: { role: AccountRole; accountId: string }) => void;
    onCreateAccount: (params: {
        role: AccountRole;
        initialName?: string;
    }) => void;
    onCreateDefaultAccount: (params: { role: AccountRole }) => void;
    onCreateDefaultAccounts: () => void;
}

export interface RequiredAccountsOptions {
    isCreateDefaultAccountsDisabled: boolean;
    hasDefaultAccountsToCreate: boolean;
}

function groupAccountsByType(
    accounts: readonly RoleMappingAccount[],
): Array<PickerSectionData<string>> {
    const accountsByType = new Map<AccountType, RoleMappingAccount[]>();
    for (const account of accounts) {
        accountsByType.set(account.type, [
            ...(accountsByType.get(account.type) ?? []),
            account,
        ]);
    }
    return [...accountsByType].map(([type, typeAccounts]) => ({
        key: type,
        title: getPredefinedGroupLabel(type),
        items: typeAccounts.map((account) => ({
            value: account.id,
            label: account.name,
        })),
    }));
}

export const RequiredAccountAssignedCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, RequiredAccountRow, unknown>) => {
        const { role, assignedAccountId, candidateAccounts } = row.original;
        const { callbacks } = useViewTableMeta<
            RequiredAccountsCallbacks,
            never
        >(table);
        const sections = useMemo(
            () => groupAccountsByType(candidateAccounts),
            [candidateAccounts],
        );

        return (
            <ViewTableCell captureClicks className={styles.assignedCell}>
                <span
                    className={editorStyles.editorCellAnchor}
                    data-editor-cell={`${role}:account`}
                >
                    <Picker.Select
                        sections={sections}
                        searchable
                        trigger="cell"
                        chevron="always"
                        className={styles.assignedTrigger}
                        value={assignedAccountId}
                        onValueChange={(accountId) => {
                            if (accountId != null) {
                                callbacks?.onAssign({ role, accountId });
                            }
                        }}
                        placeholder="Select account"
                        searchPlaceholder="Search accounts…"
                        emptyLabel="No eligible accounts"
                        renderFooter={(query) => {
                            const initialName = query.trim();
                            return (
                                <Picker.Item
                                    leading={<PlusIcon />}
                                    onSelect={() =>
                                        callbacks?.onCreateAccount({
                                            role,
                                            initialName:
                                                initialName === ""
                                                    ? undefined
                                                    : initialName,
                                        })
                                    }
                                >
                                    {initialName === ""
                                        ? "Create new account"
                                        : `Create "${initialName}"`}
                                </Picker.Item>
                            );
                        }}
                    />
                </span>
            </ViewTableCell>
        );
    },
);

RequiredAccountAssignedCell.displayName = "RequiredAccountAssignedCell";
